import { Company, UserPermission } from "fe-shared/src/types/domain";
import { FileInterface } from "fe-shared/src/types/common";
import { ButtonProps } from "fe-shared/src/components";
import { Transaction, DocumentsResponse } from "./details";
import { CorrectionMaterial } from "./materials";

export enum CorrectionsAction {
  Add = "Add",
  EditTransaction = "EditTransaction",
  EditCorrection = "EditCorrection",
  Submit = "Submit",
}

export type SelectInputOption = {
  value: number | string;
  label: string;
};

export type AdjustmentReason = {
  code: string;
  name: string;
};

export interface ScaleTicketCorrectionsForm {
  ticketId?: number | null;
  initialTicketNumber: string;
  correctedTicketNumber: string;
}

export interface CorrectionsFormContent extends Record<string, any> {
  materials: CorrectionMaterial[];
  materialAttributes: string[];
  scaleTickets?: ScaleTicketCorrectionsForm[];
  grossWeight?: number;
  tareWeight?: number;
  comment: string;
  attachments: FileInterface[];
  adjustmentReasons?: string[];
  adjustmentOtherReasonComment?: string;
}

export enum UpdatedBy {
  eSRP = "eSRP",
  Hauler = "Hauler",
  Processor = "Processor",
}

export type CorrectionMetaData<T> = T & {
  updatedByCompanyId?: number;
  updatedBy: UpdatedBy;
  modifiedDate?: string;
};

export interface CommentResponse {
  comment: string;
  correctionCommentId?: number;
}

export interface CorrectionsData extends Record<string, any> {
  materials: CorrectionMaterial[];
  materialAttributes: string[];
  scaleTickets?: ScaleTicketCorrectionsForm[];
  grossWeight?: number;
  tareWeight?: number;
  commentsData: CorrectionMetaData<CommentResponse>[];
  documentsData: CorrectionMetaData<DocumentsResponse>[];
}

export interface LastModified {
  fieldName: string;
}

export interface CorrectionStatusInfo {
  isSubmitted: boolean;
  lastModifiedBy: UpdatedBy | null;
  lastModifiedByCompanyId?: number;
}

export interface DraftCorrectionStatus extends CorrectionStatusInfo {
  isDraft: boolean;
}

export interface Corrections extends CorrectionStatusInfo {
  requestTitle?: string;
  initialData: CorrectionsData;
  correctedData: CorrectionsData;
  lastModified: CorrectionMetaData<LastModified>[];
}

export interface CorrectionsActionConfig {
  name: CorrectionsAction;
  isVisible: (
    transaction: Transaction,
    corrections: DraftCorrectionStatus,
    company?: Company
  ) => boolean;
  isDisabled?: (transaction: Transaction) => boolean;
  hasConfirmationDialog: boolean;
  buttonProps: Partial<ButtonProps>;
  dialogButtonProps?: Partial<ButtonProps>;
  permission: UserPermission;
}

export interface WeightRawResponse {
  weightTypeCode: string;
  weight: number;
  modifiedDate: string;
}

export interface MaterialsRawResponse extends CorrectionMaterial {
  materialName: string;
  modifiedDate: string;
}

export interface TicketsRawResponse {
  ticketId?: number | null;
  ticketNumber: string;
  initialTicketNumber: string;
  modifiedDate: string;
  sortOrder: number;
}

export interface AttributesRawResponse {
  correctedMaterialAttributes: string[];
  modifiedDate: string;
}

export interface CorrectionRawResponse {
  isSubmitted: boolean;
  lastModifiedBy: UpdatedBy;
  lastModifiedByCompanyId?: number;
  requestTitle: string;
  initialTareWeight: WeightRawResponse;
  correctedTareWeight: CorrectionMetaData<WeightRawResponse>;
  initialGrossWeight: WeightRawResponse;
  correctedGrossWeight: CorrectionMetaData<WeightRawResponse>;
  initialMaterials: MaterialsRawResponse[];
  correctedMaterials: CorrectionMetaData<MaterialsRawResponse>[];
  correctedDocuments: CorrectionMetaData<DocumentsResponse>[];
  correctedTickets: CorrectionMetaData<TicketsRawResponse>[];
  correctedComments: CorrectionMetaData<CommentResponse>[];
  correctedMaterialsAttributesData: CorrectionMetaData<AttributesRawResponse>;
}

export interface UpdatedChanges {
  modified: CorrectionMetaData<LastModified>[];
  comments: CorrectionMetaData<CommentResponse>[];
  documents: CorrectionMetaData<DocumentsResponse>[];
  hasChanges: boolean;
}
